import { jwtDecode } from 'jwt-decode';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { clearAllCacheExcept } from '../../../utils/functions';

type Decoded = {
  exp?: number;
  iat?: number;
  papel?: (string | number)[];
  relacoesInterface?: (string | number)[];
  isAdmin?: boolean;
  isSES?: boolean;
  isFornecedor?: boolean;
  statusInterface?: string;
  coSes?: any;
  ses?: any[];
  coFornecedor?: any;
  fornecedores?: any[];
  // Adicionar:
  vinculo?: Array<{
    status?: string;
    interface?: number;
    papel?: string;
    superAdmin?: boolean;
    [key: string]: any;
  }>;
  possuiVinculo?: boolean;
};

const INTERFACE_ROUTES: Record<number, string> = {
  1: '/daf/home/inicio',
  2: '/secsaude/home/inicio',
  3: '/fornecedor/home/inicio',
};

function getNextRouteFromTokenPayload(decoded: Decoded): string {
  if (Array.isArray(decoded?.vinculo)) {
    const vinculoAtivo = decoded.vinculo.find(
      (v: any) => String(v?.status ?? '').toLowerCase() === 'ativo',
    );

    if (vinculoAtivo?.interface) {
      const iface = Number(vinculoAtivo.interface);
      const route = INTERFACE_ROUTES[iface];
      if (route) return route;
    }
  }

  const rels: number[] = Array.isArray(decoded?.relacoesInterface)
    ? decoded.relacoesInterface.map((v: any) => Number(v))
    : [];

  if (rels.includes(3)) return INTERFACE_ROUTES[2]; // secsaude
  if (rels.includes(2)) return INTERFACE_ROUTES[3]; // fornecedor
  if (rels.includes(1)) return INTERFACE_ROUTES[1]; // daf

  return INTERFACE_ROUTES[1]; // padrão daf
}

function stripTokenParamsFromSearch(url: URL) {
  const sp = url.searchParams;
  let changed = false;
  ['access_token', 'token', 'refresh_token'].forEach((k) => {
    if (sp.has(k)) {
      sp.delete(k);
      changed = true;
    }
  });
  if (changed) {
    history.replaceState(
      {},
      document.title,
      `${url.pathname}${sp.toString() ? `?${sp.toString()}` : ''}${url.hash}`,
    );
  }
}

function stripTokenParamsFromHash(hash: string) {
  if (!hash.includes('?')) return hash;
  const [path, query] = hash.split('?');
  const params = new URLSearchParams(query || '');
  let changed = false;
  ['access_token', 'token', 'refresh_token'].forEach((k) => {
    if (params.has(k)) {
      params.delete(k);
      changed = true;
    }
  });
  return changed
    ? params.toString()
      ? `${path}?${params.toString()}`
      : path
    : hash;
}

export default function HydrationGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const [ready, setReady] = useState(false);
  const ranRef = useRef(false);

  useLayoutEffect(() => {
    if (ranRef.current) {
      setReady(true);
      return;
    }
    ranRef.current = true;

    (window as any).__hydrating = true;

    try {
      const url = new URL(window.location.href);

      let token: string | null =
        url.searchParams.get('access_token') ?? url.searchParams.get('token');
      let refresh: string | null = url.searchParams.get('refresh_token');

      if (!token || !refresh) {
        const hash = window.location.hash || '';
        const [, hashQuery = ''] = hash.split('?');
        if (hashQuery) {
          const hp = new URLSearchParams(hashQuery);
          token = token ?? hp.get('access_token') ?? hp.get('token');
          refresh = refresh ?? hp.get('refresh_token');
        }
      }

      if (token) {
        try {
          const decoded = jwtDecode<Decoded>(token);
          const now = Math.floor(Date.now() / 1000);
          const skew = 120;

          if (decoded.exp && decoded.exp + skew < now) {
            console.warn('[HydrationGate] access_token expirado; ignorando.');
          } else if (decoded.iat && decoded.iat - skew > now) {
            console.warn(
              '[HydrationGate] access_token com iat no futuro; ignorando.',
            );
          } else {
            const current = localStorage.getItem('token');
            const same = current === token;

            localStorage.setItem('token', token);
            if (refresh) localStorage.setItem('refresh_token', refresh);

            if (!same) {
              clearAllCacheExcept();
            }

            const next = getNextRouteFromTokenPayload(decoded);
            const currentHashPath = (window.location.hash || '').split('?')[0];
            if (!currentHashPath.includes(next)) {
              window.location.hash = `#${next}`;
            }
          }
        } catch (e) {
          console.error('[HydrationGate] Falha ao decodificar token:', e);
        }
      }

      stripTokenParamsFromSearch(url);
      const cleanedHash = stripTokenParamsFromHash(window.location.hash || '');
      if (cleanedHash !== window.location.hash) {
        history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${url.search}${cleanedHash}`,
        );
      }
    } finally {
      (window as any).__hydrating = false;
      setReady(true);
    }
  }, []);

  if (!ready) return null;
  return <>{children}</>;
}
